/**
 * Scenario Model - Delta-based what-if planning for org charts
 *
 * Scenarios are computed views that apply a delta (set of changes) to a base state.
 * This approach is lightweight, conflict-aware, and compatible with external data sources.
 */
import { z } from 'zod';

// =============================================================================
// Branded Types for Type-Safe IDs
// =============================================================================

/** Branded type for scenario identifiers - prevents accidental ID swaps */
export type ScenarioId = string & { readonly __brand: 'ScenarioId' };

/** Branded type for node identifiers */
export type NodeId = string & { readonly __brand: 'NodeId' };

/** Branded type for manager identifiers */
export type ManagerId = string & { readonly __brand: 'ManagerId' };

// Factory functions for ID creation
export const createScenarioId = (id: string): ScenarioId => id as ScenarioId;
export const createNodeId = (id: string): NodeId => id as NodeId;
export const createManagerId = (id: string): ManagerId => id as ManagerId;

// =============================================================================
// Org Node Types
// =============================================================================

/** Const union for org chart node types with compile-time validation */
export const ORG_NODE_TYPES = [
  'org_person_photo',
  'org_person_text',
  'org_position',
  'org_department',
] as const;

export type OrgNodeType = (typeof ORG_NODE_TYPES)[number];

/** Explicit props interface for org nodes - avoids Record<string, unknown> */
export interface OrgNodeProps {
  imageUrl?: string;
  email?: string;
  phone?: string;
  location?: string;
  startDate?: string;
  department?: string;
  role?: string;
}

// =============================================================================
// Delta Operations - Individual Change Types
// =============================================================================

/** A node being added to the org chart */
export interface NodeAddition {
  id: NodeId;
  type: OrgNodeType;
  name: string;
  title?: string;
  department?: string;
  managerId?: ManagerId;
  position?: { readonly x: number; readonly y: number };
  props?: OrgNodeProps;
}

/** A node being removed from the org chart, with original data for undo */
export interface NodeRemoval {
  nodeId: NodeId;
  originalData: {
    name: string;
    title?: string;
    managerId?: ManagerId;
    type: OrgNodeType;
    props?: OrgNodeProps;
  };
}

/** A node being moved to a different manager */
export interface NodeMove {
  nodeId: NodeId;
  oldManagerId: ManagerId | null;
  newManagerId: ManagerId | null;
}

/**
 * Discriminated union for type-safe field updates.
 * Each field has explicit old/new value types for proper validation.
 */
export type NodeUpdateField =
  | { field: 'name'; oldValue: string; newValue: string }
  | { field: 'title'; oldValue: string | undefined; newValue: string | undefined }
  | { field: 'department'; oldValue: string | undefined; newValue: string | undefined }
  | { field: 'level'; oldValue: string | undefined; newValue: string | undefined }
  | { field: 'email'; oldValue: string | undefined; newValue: string | undefined }
  | { field: 'role'; oldValue: string | undefined; newValue: string | undefined }
  // Employment dates are scenario-recordable because "Mark as leaving" is a
  // planning gesture on the live chart: the departure has to survive as a
  // pending change so the seat can be opened from it. Without a variant here
  // the delta could not represent the edit at all, and the scenario branch
  // discarded it silently.
  | { field: 'joinDate'; oldValue: string | undefined; newValue: string | undefined }
  | { field: 'departureDate'; oldValue: string | undefined; newValue: string | undefined }
  // `photoRef` and `personId` complete the set: these are the person fields
  // routed to UpdateOrgPersonCommand rather than the generic node-field
  // command, so they have no other way into a delta. An avatar swapped on a
  // live chart was discarded the same way a departure date was.
  | { field: 'photoRef'; oldValue: string | undefined; newValue: string | undefined }
  | { field: 'personId'; oldValue: string | undefined; newValue: string | undefined };

export interface BindingChange {
  sourceId: string;
  recordId: string;
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

/** A property update on an existing node */
export interface NodeUpdate {
  nodeId: NodeId;
  change: NodeUpdateField;
  bindingChange?: BindingChange;
}

/**
 * ScenarioDelta - The atomic unit of change tracking.
 * Contains all changes in a scenario organized by operation type.
 */
export interface ScenarioDelta {
  additions: NodeAddition[];
  removals: NodeRemoval[];
  moves: NodeMove[];
  updates: NodeUpdate[];
}

/** Create an empty delta */
export const createEmptyDelta = (): ScenarioDelta => ({
  additions: [],
  removals: [],
  moves: [],
  updates: [],
});

/** Check if a delta has any changes */
export const isDeltaEmpty = (delta: ScenarioDelta): boolean =>
  delta.additions.length === 0 &&
  delta.removals.length === 0 &&
  delta.moves.length === 0 &&
  delta.updates.length === 0;

/** Count total changes in a delta */
export const countDeltaChanges = (delta: ScenarioDelta): number =>
  delta.additions.length + delta.removals.length + delta.moves.length + delta.updates.length;

/**
 * Discriminated union for processing operations.
 * Useful for iterating over all changes in a delta.
 */
export type DeltaOperation =
  | { type: 'addition'; data: NodeAddition }
  | { type: 'removal'; data: NodeRemoval }
  | { type: 'move'; data: NodeMove }
  | { type: 'update'; data: NodeUpdate };

/** Convert a delta to a flat array of operations */
export const deltaToOperations = (delta: ScenarioDelta): DeltaOperation[] => [
  ...delta.additions.map((data) => ({ type: 'addition' as const, data })),
  ...delta.removals.map((data) => ({ type: 'removal' as const, data })),
  ...delta.moves.map((data) => ({ type: 'move' as const, data })),
  ...delta.updates.map((data) => ({ type: 'update' as const, data })),
];

// =============================================================================
// Scenario Status - Discriminated Union with Metadata
// =============================================================================

/** Scenario status with associated metadata for each state */
export type ScenarioStatus =
  | { status: 'draft' }
  | { status: 'proposed'; proposedAt: Date; proposedBy: string }
  | { status: 'approved'; approvedAt: Date; approvedBy: string }
  | { status: 'archived'; archivedAt: Date; reason?: string };

/** Helper to create draft status */
export const createDraftStatus = (): ScenarioStatus => ({ status: 'draft' });

// =============================================================================
// Scenario - Named Collection of Changes
// =============================================================================

/** A named scenario containing a delta and metadata */
export interface Scenario {
  id: ScenarioId;
  name: string;
  description?: string;

  // Version control
  baseSnapshotHash: string;
  baseStateVector?: string; // base64-encoded Yjs state vector for precise conflict detection
  baseTimestamp: Date;

  // The changes in this scenario
  delta: ScenarioDelta;

  // Computed status index (rebuilt on load, not persisted)
  statusIndex?: ScenarioStatusIndex;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  statusInfo: ScenarioStatus;

  // Collaboration tracking
  collaborators?: string[];

  // Conflict tracking
  lastRebaseAttempt?: Date;
  hasKnownConflicts: boolean;

  // Optional: Link to data source if connected
  dataSourceId?: string;
}

// =============================================================================
// Scenario Lifecycle State Machine
// =============================================================================

/**
 * State machine for scenario lifecycle management.
 * Controls what operations are allowed in each state.
 */
export type ScenarioLifecycleState =
  | 'VIEWING_LIVE'
  | 'VIEWING_SCENARIO'
  | 'EDITING_SCENARIO'
  | 'COMMITTING'
  | 'TRANSITIONING';

/** Valid transitions between lifecycle states */
export const LIFECYCLE_TRANSITIONS: Record<ScenarioLifecycleState, ScenarioLifecycleState[]> = {
  VIEWING_LIVE: ['VIEWING_SCENARIO', 'TRANSITIONING'],
  VIEWING_SCENARIO: ['VIEWING_LIVE', 'EDITING_SCENARIO', 'TRANSITIONING'],
  EDITING_SCENARIO: ['VIEWING_SCENARIO', 'COMMITTING'],
  COMMITTING: ['VIEWING_LIVE', 'VIEWING_SCENARIO'], // success or failure
  TRANSITIONING: ['VIEWING_LIVE', 'VIEWING_SCENARIO'],
};

/** Check if a transition is valid */
export const canTransition = (
  from: ScenarioLifecycleState,
  to: ScenarioLifecycleState
): boolean => {
  return LIFECYCLE_TRANSITIONS[from].includes(to);
};

// =============================================================================
// Pre-computed Status Index for O(1) Lookups
// =============================================================================

/** Index for O(1) node status lookups during rendering */
export interface ScenarioStatusIndex {
  added: Set<NodeId>;
  removed: Set<NodeId>;
  moved: Set<NodeId>;
  modified: Set<NodeId>;
}

/** Visual status of a node in a scenario */
export type NodeScenarioStatus = 'added' | 'removed' | 'moved' | 'modified' | 'unchanged';

/** Build a status index from a delta for O(1) lookups */
export const buildStatusIndex = (delta: ScenarioDelta): ScenarioStatusIndex => ({
  added: new Set(delta.additions.map((a) => a.id)),
  removed: new Set(delta.removals.map((r) => r.nodeId)),
  moved: new Set(delta.moves.map((m) => m.nodeId)),
  modified: new Set(delta.updates.map((u) => u.nodeId)),
});

/** Get scenario status for a node with O(1) lookup */
export const getNodeScenarioStatus = (
  nodeId: NodeId,
  index: ScenarioStatusIndex | null
): NodeScenarioStatus => {
  if (!index) return 'unchanged';
  if (index.added.has(nodeId)) return 'added';
  if (index.removed.has(nodeId)) return 'removed';
  if (index.moved.has(nodeId)) return 'moved';
  if (index.modified.has(nodeId)) return 'modified';
  return 'unchanged';
};

// =============================================================================
// Action Results - Typed Error Handling
// =============================================================================

/** Standard result type for store actions */
export type ActionResult<T = void> =
  | { success: true; data: T }
  | { success: false; error: string };

/** Create a success result */
export const success = <T>(data: T): ActionResult<T> => ({ success: true, data });

/** Create a success result with no data */
export const successVoid = (): ActionResult<void> => ({ success: true, data: undefined });

/** Create a failure result */
export const failure = <T = void>(error: string): ActionResult<T> => ({ success: false, error });

// =============================================================================
// Commit Result
// =============================================================================

/** Detail about a conflict during commit */
export interface ConflictDetail {
  nodeId: NodeId;
  operation: DeltaOperation['type'];
  reason: string;
}

/** Result of committing a scenario to the live org chart */
export interface CommitResult {
  success: boolean;
  appliedChanges: number;
  skippedChanges: number;
  conflicts?: ConflictDetail[];
  error?: string;
}

// =============================================================================
// Zod Validation Schemas
// =============================================================================

/** Zod schema for validating scenario creation input */
export const CreateScenarioSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
  description: z.string().max(500, 'Description must be 500 characters or less').optional(),
});

export type CreateScenarioInput = z.infer<typeof CreateScenarioSchema>;

/** Zod schema for node addition */
export const NodeAdditionSchema = z.object({
  id: z.string().min(1),
  type: z.enum(ORG_NODE_TYPES),
  name: z.string().min(1).max(200),
  title: z.string().max(200).optional(),
  department: z.string().max(200).optional(),
  managerId: z.string().optional(),
  position: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .optional(),
  props: z
    .object({
      imageUrl: z.string().url().optional(),
      email: z.string().email().optional(),
      phone: z.string().optional(),
      location: z.string().optional(),
      startDate: z.string().optional(),
      department: z.string().optional(),
      role: z.string().optional(),
    })
    .optional(),
});

/** Zod schema for scenario delta */
export const ScenarioDeltaSchema = z.object({
  additions: z.array(NodeAdditionSchema),
  removals: z.array(
    z.object({
      nodeId: z.string().min(1),
      originalData: z.object({
        name: z.string(),
        title: z.string().optional(),
        managerId: z.string().optional(),
        type: z.enum(ORG_NODE_TYPES),
        props: z
          .object({
            imageUrl: z.string().optional(),
            email: z.string().optional(),
            phone: z.string().optional(),
            location: z.string().optional(),
            startDate: z.string().optional(),
            department: z.string().optional(),
            role: z.string().optional(),
          })
          .optional(),
      }),
    })
  ),
  moves: z.array(
    z.object({
      nodeId: z.string().min(1),
      oldManagerId: z.string().nullable(),
      newManagerId: z.string().nullable(),
    })
  ),
  updates: z.array(
    z.object({
      nodeId: z.string().min(1),
      change: z.discriminatedUnion('field', [
        z.object({
          field: z.literal('name'),
          oldValue: z.string(),
          newValue: z.string(),
        }),
        z.object({
          field: z.literal('title'),
          oldValue: z.string().optional(),
          newValue: z.string().optional(),
        }),
        z.object({
          field: z.literal('department'),
          oldValue: z.string().optional(),
          newValue: z.string().optional(),
        }),
        z.object({
          field: z.literal('email'),
          oldValue: z.string().optional(),
          newValue: z.string().optional(),
        }),
        z.object({
          field: z.literal('role'),
          oldValue: z.string().optional(),
          newValue: z.string().optional(),
        }),
      ]),
    })
  ),
});
